/**
 * particleDensityGrid.ts
 *
 * A coarse, approximately equal-area binning of the sphere (same spirit as
 * the "reduced" mesh grid in geometryUtils.ts: latitude bands uniform in
 * sin(lat), with each band's longitude bin count tapered by cos(lat)) used
 * to keep the wind-particle population roughly uniformly spread over the
 * globe even though advection naturally clumps particles into convergence
 * zones and empties out divergence zones.
 *
 * Each frame, particle positions are binned into this grid. Respawns are
 * then biased toward under-populated cells, and particles sitting in
 * significantly over-populated cells may be retired early, so the system
 * actively creates and destroys particles to chase a target density per
 * cell. That target is usually uniform, EXCEPT cells over low-pressure
 * systems get a boosted target (see LOW_PRESSURE_DENSITY_BOOST_* in
 * constants.ts) so cyclonic convergence zones -- the most interesting place
 * to watch advection -- are allowed to visibly crowd with particles instead
 * of being thinned out like everywhere else.
 */

import {
  DENSITY_GRID_SUBSAMPLES_PER_AXIS,
  LOW_PRESSURE_DENSITY_BOOST_FACTOR,
  LOW_PRESSURE_DENSITY_BOOST_FULL_HPA,
  LOW_PRESSURE_DENSITY_BOOST_START_HPA
} from "../constants";
import type { MslpSamplerFn } from "../physics/windSampler";

const DEG2RAD = Math.PI / 180;

/** A cell is considered "overloaded" once its count exceeds this multiple of its target. */
const OVERLOAD_FACTOR = 2.0;
/** Minimum spawn weight for any cell, so even full cells retain a small chance of being picked. */
const MIN_SPAWN_WEIGHT = 1e-3;

export class ParticleDensityGrid {
  private readonly latBandCount: number;
  private readonly lonBinCount: Int32Array;
  private readonly cellStartIndex: Int32Array;
  private readonly totalCells: number;
  private readonly counts: Int32Array;
  private readonly cumulativeSpawnWeight: Float64Array;
  /** Per-cell target-density multiplier, 1x everywhere except boosted over low-pressure cells. */
  private readonly densityMultiplier: Float32Array;
  private targetPerCell = 0;

  constructor(latBandCount: number, baseLonBins: number, sampleMslpHpa?: MslpSamplerFn) {
    this.latBandCount = latBandCount;
    this.lonBinCount = new Int32Array(latBandCount);
    this.cellStartIndex = new Int32Array(latBandCount + 1);

    let runningIndex = 0;
    for (let band = 0; band < latBandCount; band++) {
      const sinLo = -1 + (2 * band) / latBandCount;
      const sinHi = -1 + (2 * (band + 1)) / latBandCount;
      const centerLatRad = Math.asin((sinLo + sinHi) / 2);
      const bins = Math.max(4, Math.round(baseLonBins * Math.cos(centerLatRad)));
      this.lonBinCount[band] = bins;
      this.cellStartIndex[band] = runningIndex;
      runningIndex += bins;
    }
    this.cellStartIndex[latBandCount] = runningIndex;
    this.totalCells = runningIndex;
    this.counts = new Int32Array(this.totalCells);
    this.cumulativeSpawnWeight = new Float64Array(this.totalCells);
    this.densityMultiplier = new Float32Array(this.totalCells).fill(1);

    if (sampleMslpHpa) {
      this.precomputeDensityMultipliers(sampleMslpHpa);
    }
  }

  /** MSLP varies slowly compared to wind, and this prototype uses a single static GFS cycle, so
   *  each cell's multiplier is computed once rather than every frame. Each cell is sampled over
   *  a small sub-grid (not just its center point) and the MINIMUM MSLP found is used, since
   *  these cells are much wider than a small/tight storm -- a single center sample could miss
   *  one entirely if it's off-center, or dilute its effect if it's small relative to the cell.
   *  See DENSITY_GRID_SUBSAMPLES_PER_AXIS in constants.ts. */
  private precomputeDensityMultipliers(sampleMslpHpa: MslpSamplerFn): void {
    const subN = DENSITY_GRID_SUBSAMPLES_PER_AXIS;
    for (let band = 0; band < this.latBandCount; band++) {
      const sinLo = -1 + (2 * band) / this.latBandCount;
      const sinHi = -1 + (2 * (band + 1)) / this.latBandCount;
      const bins = this.lonBinCount[band];
      for (let lonBin = 0; lonBin < bins; lonBin++) {
        const lonFracLo = lonBin / bins;
        const lonFracHi = (lonBin + 1) / bins;
        let minMslpHpa = Infinity;
        for (let sy = 0; sy < subN; sy++) {
          const sinSample = sinLo + ((sy + 0.5) / subN) * (sinHi - sinLo);
          const latSample = (Math.asin(Math.min(1, Math.max(-1, sinSample))) * 180) / Math.PI;
          for (let sx = 0; sx < subN; sx++) {
            const lonFracSample = lonFracLo + ((sx + 0.5) / subN) * (lonFracHi - lonFracLo);
            const lonSample = -180 + lonFracSample * 360;
            const mslpHpa = sampleMslpHpa(latSample, lonSample);
            if (mslpHpa < minMslpHpa) minMslpHpa = mslpHpa;
          }
        }
        const lowT = Math.min(
          1,
          Math.max(
            0,
            (LOW_PRESSURE_DENSITY_BOOST_START_HPA - minMslpHpa) /
              (LOW_PRESSURE_DENSITY_BOOST_START_HPA - LOW_PRESSURE_DENSITY_BOOST_FULL_HPA)
          )
        );
        this.densityMultiplier[this.cellStartIndex[band] + lonBin] =
          1 + (LOW_PRESSURE_DENSITY_BOOST_FACTOR - 1) * lowT;
      }
    }
  }

  private bandForLat(latDeg: number): number {
    const sinLat = Math.sin(latDeg * DEG2RAD);
    const band = Math.floor(((sinLat + 1) / 2) * this.latBandCount);
    return Math.max(0, Math.min(this.latBandCount - 1, band));
  }

  private lonBinForBand(band: number, lonDeg: number): number {
    const lonFrac = (lonDeg + 180) / 360;
    const bins = this.lonBinCount[band];
    const bin = Math.floor(lonFrac * bins);
    return Math.max(0, Math.min(bins - 1, bin));
  }

  cellIndexFor(latDeg: number, lonDeg: number): number {
    const band = this.bandForLat(latDeg);
    return this.cellStartIndex[band] + this.lonBinForBand(band, lonDeg);
  }

  /** Rebuilds the occupancy histogram and spawn-weight distribution from current positions. */
  recompute(latDeg: Float32Array, lonDeg: Float32Array, particleCount: number): void {
    this.counts.fill(0);
    for (let i = 0; i < particleCount; i++) {
      this.counts[this.cellIndexFor(latDeg[i], lonDeg[i])]++;
    }

    this.targetPerCell = particleCount / this.totalCells;

    let cumulative = 0;
    for (let cell = 0; cell < this.totalCells; cell++) {
      const effectiveTarget = this.targetPerCell * this.densityMultiplier[cell];
      const deficit = effectiveTarget - this.counts[cell];
      cumulative += Math.max(MIN_SPAWN_WEIGHT, deficit);
      this.cumulativeSpawnWeight[cell] = cumulative;
    }
  }

  isOverloaded(latDeg: number, lonDeg: number): boolean {
    const cell = this.cellIndexFor(latDeg, lonDeg);
    return this.counts[cell] > OVERLOAD_FACTOR * this.targetPerCell * this.densityMultiplier[cell];
  }

  /** Picks a (lat, lon) biased toward currently under-populated cells. */
  sampleUnderpopulatedLatLon(): [number, number] {
    const total = this.cumulativeSpawnWeight[this.totalCells - 1];
    const target = Math.random() * total;

    // Binary search for the first cumulative weight >= target.
    let lo = 0;
    let hi = this.totalCells - 1;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (this.cumulativeSpawnWeight[mid] >= target) {
        hi = mid;
      } else {
        lo = mid + 1;
      }
    }
    const cell = lo;

    // Find which band this cell belongs to (cellStartIndex is sorted, so binary search works here too).
    let bandLo = 0;
    let bandHi = this.latBandCount - 1;
    while (bandLo < bandHi) {
      const mid = (bandLo + bandHi + 1) >>> 1;
      if (this.cellStartIndex[mid] <= cell) {
        bandLo = mid;
      } else {
        bandHi = mid - 1;
      }
    }
    const band = bandLo;
    const lonBin = cell - this.cellStartIndex[band];
    const bins = this.lonBinCount[band];

    const sinLo = -1 + (2 * band) / this.latBandCount;
    const sinHi = -1 + (2 * (band + 1)) / this.latBandCount;
    const latDeg = (Math.asin(sinLo + Math.random() * (sinHi - sinLo)) * 180) / Math.PI;

    const lonFracLo = lonBin / bins;
    const lonFracHi = (lonBin + 1) / bins;
    const lonDeg = -180 + (lonFracLo + Math.random() * (lonFracHi - lonFracLo)) * 360;

    return [latDeg, lonDeg];
  }
}
